// frontend/src/components/StuntGroupList.jsx
import React, { useState, useEffect, useRef } from 'react';
import StuntGroupService from '../services/StuntGroupService';
import StuntService from '../services/StuntService';
import SectionList from './SectionList';
import wsAction from '../utils/wsActions';
import { UndoRedoHelpers } from '../hooks/useUndoRedo';

function StuntGroupList({ sessionId, dancers, stunts: stuntsProp, stuntGroups: stuntGroupsProp, onStuntGroupsChange, onStuntsChange, onGroupSelect, wsSend, user, sortMode, onSortChange, recordAction, isInUndoRedo }) {
  const [stuntGroups, setStuntGroups] = useState(stuntGroupsProp || []);
  const [stunts, setStunts] = useState(stuntsProp || []);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(null);

  // État pour le modal
  const [selectedDancers, setSelectedDancers] = useState([]);
  const [selectedStunts, setSelectedStunts] = useState([]);
  const [newStuntName, setNewStuntName] = useState('');
  
  // Rotation des couleurs pour les stunts
  const stuntColors = [
    "#36949d", "#ff924c", "#1982c4", "#ffca3a",
    "#4267ac", "#c5ca30", "#565aa0", "#8ac926", "#6a4c93",
    "#e97c61", "#e9c46a", "#e76f51", "#8ab17d", "#ee8959",
    "#2a9d8f", "#f4a261", "#287271", "#efb366", "#264653", "#ff595e"
  ];
  const [colorIndex, setColorIndex] = useState(0);
  const [newStuntColor, setNewStuntColor] = useState(stuntColors[0]);
  const [stuntNameError, setStuntNameError] = useState('');
  
  const [dancerSort, setDancerSort] = useState('asc'); // 'none', 'asc', 'desc'
  const [stuntSort, setStuntSort] = useState('asc'); // 'none', 'asc', 'desc'
  const [deletingStuntId, setDeletingStuntId] = useState(null); // Pour la confirmation de suppression

  // États pour les ombres de scroll
  const [dancerShadowTop, setDancerShadowTop] = useState(false);
  const [dancerShadowBottom, setDancerShadowBottom] = useState(false);
  const [stuntShadowTop, setStuntShadowTop] = useState(false);
  const [stuntShadowBottom, setStuntShadowBottom] = useState(false);

  // Refs pour les conteneurs scrollables
  const dancerListRef = useRef(null);
  const stuntListRef = useRef(null);

  // ✅ Mettre à jour les states quand les props changent
  useEffect(() => {
    if (stuntGroupsProp) {
      setStuntGroups(stuntGroupsProp);
      
      // Si le modal est ouvert et que le groupe a été mis à jour, actualiser le modal
      if (currentGroup && showModal) {
        const updatedGroup = stuntGroupsProp.find(g => g.id === currentGroup.id);
        if (updatedGroup) {
          setCurrentGroup(updatedGroup);
          setSelectedDancers(updatedGroup.dancers?.map(d => d.id) || []);
          setSelectedStunts(updatedGroup.stunts?.map(s => s.id) || []);
        }
      }
    }
  }, [stuntGroupsProp]);

  useEffect(() => {
    if (stuntsProp) {
      setStunts(stuntsProp);
    }
  }, [stuntsProp]);

  const addStuntGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      const result = await wsAction.createStuntGroup(
        wsSend,
        {
          session_id: sessionId,
          name: newGroupName
        },
        user.access_token
      );
      
      // ✅ Enregistrer pour undo/redo
      if (result && result.stunt_group && recordAction && (!isInUndoRedo || !isInUndoRedo())) {
        const { undoData, redoData, description } = UndoRedoHelpers.stuntGroupCreate(
          result.stunt_group, sessionId
        );
        recordAction('stunt_group_create', undoData, redoData, description);
      }
      
      setNewGroupName('');
    } catch (error) {
      console.error('Erreur lors de la création du groupe:', error);
      alert('Erreur lors de la création du groupe');
    }
  };

  const deleteStuntGroup = async (groupId) => {
    try {
      // Récupérer le groupe avant suppression pour l'undo
      const groupToDelete = stuntGroups.find(g => g.id === groupId);
      
      const result = await wsAction.deleteStuntGroup(
        wsSend,
        {
          session_id: sessionId,
          group_id: groupId
        },
        user.access_token
      );
      
      // ✅ Enregistrer pour undo/redo
      if (result && groupToDelete && recordAction && (!isInUndoRedo || !isInUndoRedo())) {
        const { undoData, redoData, description } = UndoRedoHelpers.stuntGroupDelete(
          groupToDelete, sessionId
        );
        recordAction('stunt_group_delete', undoData, redoData, description);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du groupe:', error);
    }
  };

  const openGroupModal = (group) => {
    setCurrentGroup(group);
    // ✅ Extraire les IDs depuis les objets dancers et stunts
    setSelectedDancers(group.dancers?.map(d => d.id) || []);
    setSelectedStunts(group.stunts?.map(s => s.id) || []);
    
    // Trouver la prochaine couleur non utilisée
    const usedColors = stunts.filter(s => s.color).map(s => s.color.toLowerCase());
    const availableColors = stuntColors.filter(color => !usedColors.includes(color.toLowerCase()));
    
    if (availableColors.length > 0) {
      // S'il reste des couleurs disponibles, utiliser la première
      setNewStuntColor(availableColors[0]);
      setColorIndex(stuntColors.indexOf(availableColors[0]));
    } else {
      // Si toutes les couleurs sont utilisées, recommencer depuis le début
      setNewStuntColor(stuntColors[0]);
      setColorIndex(0);
    }
    
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentGroup(null);
    setSelectedDancers([]);
    setSelectedStunts([]);
    setNewStuntName('');
    setStuntNameError('');
    setDancerSort('asc');
    setStuntSort('asc');
    setDeletingStuntId(null);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!showModal) return;
      
      if (e.key === 'Escape') {
        closeModal();
      }
      
      // Enter pour fermer, sauf si on est sur l'input de création de stunt
      if (e.key === 'Enter') {
        const activeElement = document.activeElement;
        const isStuntInput = activeElement && (
          activeElement.id === 'newStuntName' || 
          activeElement.placeholder === 'Nom du stunt'
        );
        
        if (!isStuntInput) {
          e.preventDefault();
          closeModal();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showModal]);

  // Initialiser les ombres de scroll quand le modal s'ouvre ou que les listes changent
  useEffect(() => {
    if (showModal) {
      setTimeout(() => {
        if (dancerListRef.current) {
          updateScrollShadows(dancerListRef.current, setDancerShadowTop, setDancerShadowBottom);
        }
        if (stuntListRef.current) {
          updateScrollShadows(stuntListRef.current, setStuntShadowTop, setStuntShadowBottom);
        }
      }, 0);
    }
  }, [showModal, dancers.length, stunts.length, dancerSort, stuntSort]);

  const toggleDancer = async (dancerId) => {
    const isSelected = selectedDancers.includes(dancerId);
    const oldDancerIds = [...selectedDancers]; // Copie avant modification
    try {
      if (isSelected) {
        setSelectedDancers(selectedDancers.filter(id => id !== dancerId));
      } else {
        setSelectedDancers([...selectedDancers, dancerId]);
      }
      // Envoyer la liste complète via WebSocket
      const newDancerIds = isSelected 
        ? selectedDancers.filter(id => id !== dancerId)
        : [...selectedDancers, dancerId];
      
      const result = await wsAction.addDancersToStuntGroup(
        wsSend,
        {
          session_id: sessionId,
          group_id: currentGroup.id,
          dancer_ids: newDancerIds
        },
        user.access_token
      );
      
      // ✅ Enregistrer pour undo/redo
      if (result && recordAction && (!isInUndoRedo || !isInUndoRedo())) {
        const { undoData, redoData, description } = UndoRedoHelpers.stuntGroupAddDancers(
          currentGroup.id, oldDancerIds, newDancerIds, sessionId
        );
        recordAction('stunt_group_add_dancers', undoData, redoData, description);
      }
    } catch (error) {
      console.error('Erreur lors de la modification des danseurs:', error);
    }
  };

  const toggleStunt = async (stuntId) => {
    const isSelected = selectedStunts.includes(stuntId);
    const oldStuntIds = [...selectedStunts]; // Copie avant modification
    try {
      if (isSelected) {
        setSelectedStunts(selectedStunts.filter(id => id !== stuntId));
      } else {
        setSelectedStunts([...selectedStunts, stuntId]);
      }
      // Envoyer la liste complète via WebSocket
      const newStuntIds = isSelected 
        ? selectedStunts.filter(id => id !== stuntId)
        : [...selectedStunts, stuntId];
      
      const result = await wsAction.addStuntsToStuntGroup(
        wsSend,
        {
          session_id: sessionId,
          group_id: currentGroup.id,
          stunt_ids: newStuntIds
        },
        user.access_token
      );
      
      // ✅ Enregistrer pour undo/redo
      if (result && recordAction && (!isInUndoRedo || !isInUndoRedo())) {
        const { undoData, redoData, description } = UndoRedoHelpers.stuntGroupAddStunts(
          currentGroup.id, oldStuntIds, newStuntIds, sessionId
        );
        recordAction('stunt_group_add_stunts', undoData, redoData, description);
      }
    } catch (error) {
      console.error('Erreur lors de la modification des stunts:', error);
    }
  };

    const createAndAddStunt = async () => {
    if (!newStuntName.trim()) return;
    
    // Vérifier si le nom existe déjà
    const nameExists = stunts.some(stunt => stunt.name.toLowerCase() === newStuntName.trim().toLowerCase());
    if (nameExists) {
      setStuntNameError('Ce nom est déjà utilisé');
      return;
    }
    
    setStuntNameError('');
    try {
      const oldStuntIds = [...selectedStunts]; // Copie avant modification
      
      const response = await wsAction.createStunt(
        wsSend,
        {
          session_id: sessionId,
          name: newStuntName,
          color: newStuntColor
        },
        user.access_token
      );
      const newStunt = response.stunt;
      
      // ✅ Enregistrer la création du stunt pour undo/redo
      if (newStunt && recordAction && (!isInUndoRedo || !isInUndoRedo())) {
        const { undoData, redoData, description } = UndoRedoHelpers.stuntCreate(
          newStunt, sessionId
        );
        recordAction('stunt_create', undoData, redoData, description);
      }
      
      // Ajouter le stunt au groupe
      const newStuntIds = [...selectedStunts, newStunt.id];
      const addResult = await wsAction.addStuntsToStuntGroup(
        wsSend,
        {
          session_id: sessionId,
          group_id: currentGroup.id,
          stunt_ids: newStuntIds
        },
        user.access_token
      );
      
      // ✅ Enregistrer l'ajout au groupe pour undo/redo
      if (addResult && recordAction && (!isInUndoRedo || !isInUndoRedo())) {
        const { undoData, redoData, description } = UndoRedoHelpers.stuntGroupAddStunts(
          currentGroup.id, oldStuntIds, newStuntIds, sessionId
        );
        recordAction('stunt_group_add_stunts', undoData, redoData, description);
      }
      
      setNewStuntName('');
      
      // Passer à la couleur suivante non utilisée
      const usedColors = [...stunts, newStunt].filter(s => s.color).map(s => s.color.toLowerCase());
      const availableColors = stuntColors.filter(color => !usedColors.includes(color.toLowerCase()));
      
      if (availableColors.length > 0) {
        setNewStuntColor(availableColors[0]);
        setColorIndex(stuntColors.indexOf(availableColors[0]));
      } else {
        // Si toutes les couleurs sont utilisées, continuer la rotation normale
        const nextIndex = (colorIndex + 1) % stuntColors.length;
        setColorIndex(nextIndex);
        setNewStuntColor(stuntColors[nextIndex]);
      }
      
      if (onStuntsChange) onStuntsChange();
      if (onStuntGroupsChange) onStuntGroupsChange();
      setSelectedStunts([...selectedStunts, newStunt.id]);
    } catch (error) {
      console.error('Erreur lors de la création du stunt:', error);
      if (error.response?.status === 400) {
        setStuntNameError('Ce nom est déjà utilisé');
      } else {
        setStuntNameError('Erreur lors de la création du stunt');
      }
    }
  };

  const getSortedDancers = () => {
    const sorted = [...dancers];
    if (dancerSort === 'asc') {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    } else if (dancerSort === 'desc') {
      sorted.sort((a, b) => b.name.localeCompare(a.name));
    }
    return sorted;
  };

  const getSortedStunts = () => {
    const sorted = [...stunts];
    if (stuntSort === 'asc') {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    } else if (stuntSort === 'desc') {
      sorted.sort((a, b) => b.name.localeCompare(a.name));
    }
    return sorted;
  };

  const toggleDancerSort = () => {
    setDancerSort(prev => prev === 'none' ? 'asc' : prev === 'asc' ? 'desc' : 'none');
  };

  const toggleStuntSort = () => {
    setStuntSort(prev => prev === 'none' ? 'asc' : prev === 'asc' ? 'desc' : 'none');
  };

  const deleteStunt = async (stuntId) => {
    try {
      // Récupérer le stunt avant suppression pour l'undo
      const stuntToDelete = stunts.find(s => s.id === stuntId);
      
      setSelectedStunts(selectedStunts.filter(id => id !== stuntId));
      setDeletingStuntId(null);
      
      const result = await wsAction.deleteStunt(
        wsSend,
        {
          session_id: sessionId,
          stunt_id: stuntId
        },
        user.access_token
      );
      
      // ✅ Enregistrer pour undo/redo
      if (result && stuntToDelete && recordAction && (!isInUndoRedo || !isInUndoRedo())) {
        const { undoData, redoData, description } = UndoRedoHelpers.stuntDelete(
          stuntToDelete, sessionId
        );
        recordAction('stunt_delete', undoData, redoData, description);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du stunt:', error);
      alert('Erreur lors de la suppression du stunt');
    }
  };

  // Fonction pour mettre à jour les ombres de scroll
  const updateScrollShadows = (element, setTop, setBottom) => {
    if (!element) return;
    setTop(element.scrollTop > 0);
    setBottom(element.scrollTop + element.clientHeight < element.scrollHeight);
  };

  // Fonction de tri alphabétique
  const sortData = (data, mode) => {
    if (!mode || mode === 'default') return data;
    return [...data].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      if (mode === 'asc') return nameA.localeCompare(nameB);
      return nameB.localeCompare(nameA);
    });
  };

  // Composant bouton de tri
  const SortButton = () => {
    const cycleSort = () => {
      if (!sortMode || sortMode === 'default') onSortChange('asc');
      else if (sortMode === 'asc') onSortChange('desc');
      else onSortChange('default');
    };

    return (
      <button
        onClick={cycleSort}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '2px 6px',
          fontSize: '0.75rem',
          color: !sortMode || sortMode === 'default' ? '#999' : '#333',
          fontWeight: !sortMode || sortMode === 'default' ? 'normal' : '600',
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          borderRadius: '4px',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0e0e0'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        title={!sortMode || sortMode === 'default' ? 'Tri par défaut' : sortMode === 'asc' ? 'A → Z' : 'Z → A'}
      >
        {!sortMode || sortMode === 'default' ? (
          <>A-Z<span style={{ fontSize: '0.7rem' }}>•</span></>
        ) : sortMode === 'asc' ? (
          <>A-Z<span style={{ fontSize: '0.6rem' }}>↓</span></>
        ) : (
          <>Z-A<span style={{ fontSize: '0.6rem' }}>↑</span></>
        )}
      </button>
    );
  };

  return (
    <>
      <SectionList
        title="Stunt Groups"
        titleExtra={onSortChange && <SortButton />}
        data={sortData(stuntGroups, sortMode)}
        selectedId={selectedGroupId}
        onSelect={(groupId) => {
          setSelectedGroupId(groupId);
          const group = stuntGroups.find(g => g.id === groupId);
          if (group) {
            openGroupModal(group);
            if (onGroupSelect) onGroupSelect(group);
          }
        }}
        onAdd={addStuntGroup}
        addPlaceholder="Nom du groupe"
        addValue={newGroupName}
        setAddValue={setNewGroupName}
        onDelete={deleteStuntGroup}
        renderItem={(group) => (
          <span style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: '1',
                minWidth: 0,
              }}
              title={`${group.name} (${group.dancers?.length || 0} danseurs, ${group.stunts?.length || 0} stunts)`}
            >
              {group.name}
            </span>
            <span style={{ marginLeft: '8px', fontSize: '0.85em', color: '#888' }}>
              👥 {group.dancers?.length || 0} ⭐ {group.stunts?.length || 0}
            </span>
          </span>
        )}
      />

      {/* Modal de gestion du groupe */}
      {showModal && currentGroup && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(2px)',
          }}
          onClick={closeModal}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              padding: '0',
              borderRadius: '16px',
              maxWidth: '800px',
              width: '90%',
              maxHeight: '95vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '24px 32px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              borderRadius: '16px 16px 0 0',
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: '600',
                color: '#111827',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <span>✨ {currentGroup.name}</span>
                <button
                  onClick={closeModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    color: '#9ca3af',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    lineHeight: '1',
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#374151'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                >
                  ✕
                </button>
              </h2>
              <p style={{
                margin: '8px 0 0 0',
                fontSize: '14px',
                color: '#6b7280',
              }}>
                Gérez les danseurs et stunts de ce groupe
              </p>
            </div>

            {/* Content */}
            <div style={{ padding: '24px 120px', overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>

              {/* Section Danseurs */}
              <div style={{ marginBottom: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                    👥 Danseurs ({selectedDancers.length}/{dancers.length})
                  </h3>
                  <button
                    onClick={toggleDancerSort}
                    style={{
                      background: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#374151',
                      transition: 'all 0.2s',
                      minWidth: '80px',
                      height: '32px',
                      justifyContent: 'center',
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  >
                    {dancerSort === 'none' && '⇅ Trier'}
                    {dancerSort === 'asc' && '↑ A-Z'}
                    {dancerSort === 'desc' && '↓ Z-A'}
                  </button>
                </div>
                {dancers.length === 0 ? (
                  <p style={{ color: '#9ca3af', fontStyle: 'italic', margin: '16px 0', textAlign: 'center' }}>
                    Aucun danseur disponible
                  </p>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <div 
                      ref={dancerListRef}
                      onScroll={(e) => updateScrollShadows(e.target, setDancerShadowTop, setDancerShadowBottom)}
                      style={{
                        maxHeight: '35vh',
                        overflow: 'auto',
                        border: '1px solid #e5e7eb',
                        borderRadius: '10px',
                        padding: '6px 8px',
                        backgroundColor: '#fafafa',
                      }}>
                      {getSortedDancers().map((dancer) => (
                      <label
                        key={dancer.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '6px 8px',
                          cursor: 'pointer',
                          borderRadius: '6px',
                          marginBottom: '4px',
                          backgroundColor: selectedDancers.includes(dancer.id) ? '#dbeafe' : '#ffffff',
                          border: selectedDancers.includes(dancer.id) ? '1px solid #3b82f6' : '1px solid transparent',
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => {
                          if (!selectedDancers.includes(dancer.id)) {
                            e.currentTarget.style.backgroundColor = '#f3f4f6';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!selectedDancers.includes(dancer.id)) {
                            e.currentTarget.style.backgroundColor = '#ffffff';
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedDancers.includes(dancer.id)}
                          onChange={() => toggleDancer(dancer.id)}
                          style={{
                            marginRight: '10px',
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                            accentColor: '#3b82f6',
                          }}
                        />
                        <span style={{ fontSize: '14px', color: '#111827', fontWeight: selectedDancers.includes(dancer.id) ? '500' : '400' }}>
                          {dancer.name}
                        </span>
                      </label>
                    ))}
                  </div>
                  {/* Overlays de dégradé pour les danseurs */}
                  {dancerShadowTop && (
                    <div style={{
                      position: 'absolute',
                      top: '0',
                      left: '0',
                      right: '0',
                      height: '30px',
                      background: 'linear-gradient(to bottom, rgba(250,250,250,1) 0%, rgba(15, 15, 15, 0) 100%)',
                      pointerEvents: 'none',
                      borderRadius: '10px 10px 0 0',
                    }} />
                  )}
                  {dancerShadowBottom && (
                    <div style={{
                      position: 'absolute',
                      bottom: '0',
                      left: '0',
                      right: '0',
                      height: '30px',
                      background: 'linear-gradient(to top, rgba(250,250,250,1) 0%, rgba(250,250,250,0) 100%)',
                      pointerEvents: 'none',
                      borderRadius: '0 0 10px 10px',
                    }} />
                  )}
                </div>
                )}
              </div>

              {/* Section Stunts */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                    ⭐ Stunts ({selectedStunts.length}/{stunts.length})
                  </h3>
                  <button
                    onClick={toggleStuntSort}
                    style={{
                      background: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#374151',
                      transition: 'all 0.2s',
                      minWidth: '80px',
                      height: '32px',
                      justifyContent: 'center',
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  >
                    {stuntSort === 'none' && '⇅ Trier'}
                    {stuntSort === 'asc' && '↑ A-Z'}
                    {stuntSort === 'desc' && '↓ Z-A'}
                  </button>
                </div>
                {stunts.length === 0 && selectedStunts.length === 0 ? (
                  <p style={{ color: '#9ca3af', fontStyle: 'italic', margin: '16px 0', textAlign: 'center' }}>
                    Aucun stunt disponible
                  </p>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <div 
                      ref={stuntListRef}
                      onScroll={(e) => updateScrollShadows(e.target, setStuntShadowTop, setStuntShadowBottom)}
                      style={{
                        maxHeight: '35vh',
                        overflow: 'auto',
                        border: '1px solid #e5e7eb',
                        borderRadius: '10px',
                        padding: '6px 8px',
                        backgroundColor: '#fafafa',
                      }}>
                      {getSortedStunts().map((stunt) => (
                      <label
                        key={stunt.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '6px 8px',
                          cursor: 'pointer',
                          borderRadius: '6px',
                          marginBottom: '4px',
                          backgroundColor: selectedStunts.includes(stunt.id) ? '#fef3c7' : '#ffffff',
                          border: selectedStunts.includes(stunt.id) ? '1px solid #f59e0b' : '1px solid transparent',
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => {
                          if (!selectedStunts.includes(stunt.id)) {
                            e.currentTarget.style.backgroundColor = '#f3f4f6';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!selectedStunts.includes(stunt.id)) {
                            e.currentTarget.style.backgroundColor = '#ffffff';
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedStunts.includes(stunt.id)}
                          onChange={() => toggleStunt(stunt.id)}
                          style={{
                            marginRight: '10px',
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                            accentColor: '#f59e0b',
                          }}
                        />
                        <span
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: stunt.color,
                            marginRight: '10px',
                            flexShrink: 0,
                            border: '2px solid rgba(0,0,0,0.1)',
                          }}
                        />
                        <span style={{ fontSize: '14px', color: '#111827', flex: 1, fontWeight: selectedStunts.includes(stunt.id) ? '500' : '400' }}>
                          {stunt.name}
                        </span>
                        {deletingStuntId === stunt.id ? (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              deleteStunt(stunt.id);
                            }}
                            style={{
                              background: '#dc2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '11px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              marginLeft: '8px',
                              flexShrink: 0,
                            }}
                          >
                            Confirmer
                          </button>
                        ) : (
                          <span
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDeletingStuntId(stunt.id);
                            }}
                            style={{
                              fontSize: '18px',
                              color: '#dc2626',
                              cursor: 'pointer',
                              marginLeft: '8px',
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '50%',
                              transition: 'background-color 0.2s',
                              flexShrink: 0,
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#fee2e2'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            ×
                          </span>
                        )}
                      </label>
                      ))}
                    </div>
                    {/* Overlays de dégradé */}
                    {stuntShadowTop && (
                      <div style={{
                        position: 'absolute',
                        top: '0',
                        left: '0',
                        right: '0',
                        height: '30px',
                        background: 'linear-gradient(to bottom, rgba(250,250,250,1) 0%, rgba(250,250,250,0) 100%)',
                        pointerEvents: 'none',
                        borderRadius: '10px 10px 0 0',
                      }} />
                    )}
                    {stuntShadowBottom && (
                      <div style={{
                        position: 'absolute',
                        bottom: '0',
                        left: '0',
                        right: '0',
                        height: '30px',
                        background: 'linear-gradient(to top, rgba(250,250,250,1) 0%, rgba(250,250,250,0) 100%)',
                        pointerEvents: 'none',
                        borderRadius: '0 0 10px 10px',
                      }} />
                    )}
                  </div>
                )}

                {/* Créer un nouveau stunt */}
                <div style={{
                  marginTop: '16px',
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                }}>
                  <h4 style={{
                    margin: '0 0 12px 0',
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#374151',
                  }}>
                    ➕ Créer un nouveau stunt
                  </h4>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'stretch' }}>
                    <input
                      id="newStuntName"
                      type="text"
                      placeholder="Nom du stunt..."
                      value={newStuntName}
                      onChange={(e) => {
                        setNewStuntName(e.target.value);
                        if (stuntNameError) setStuntNameError('');
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          createAndAddStunt();
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: stuntNameError ? '2px solid #dc2626' : '1px solid #d1d5db',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                        backgroundColor: stuntNameError ? '#fef2f2' : 'white',
                      }}
                      onFocus={(e) => {
                        if (!stuntNameError) e.target.style.borderColor = '#3b82f6';
                      }}
                      onBlur={(e) => {
                        if (!stuntNameError) e.target.style.borderColor = '#d1d5db';
                      }}
                    />
                    <input
                      type="color"
                      value={newStuntColor}
                      onChange={(e) => setNewStuntColor(e.target.value)}
                      style={{
                        width: '46px',
                        height: '46px',
                        border: '2px solid #d1d5db',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        padding: '2px',
                      }}
                    />
                    <button
                      onClick={createAndAddStunt}
                      disabled={!newStuntName.trim()}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: newStuntName.trim() ? '#10b981' : '#d1d5db',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: newStuntName.trim() ? 'pointer' : 'not-allowed',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.2s',
                        whiteSpace: 'nowrap',
                      }}
                      onMouseEnter={(e) => {
                        if (newStuntName.trim()) e.target.style.backgroundColor = '#059669';
                      }}
                      onMouseLeave={(e) => {
                        if (newStuntName.trim()) e.target.style.backgroundColor = '#10b981';
                      }}
                    >
                      Créer
                    </button>
                  </div>
                  {stuntNameError ? (
                    <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#dc2626', fontWeight: '500' }}>
                      ⚠️ {stuntNameError}
                    </p>
                  ) : (
                    <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#9ca3af' }}>
                      💡 Appuyez sur Entrée pour créer rapidement
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer avec statistiques */}
            <div style={{
              padding: '20px 32px',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              borderRadius: '0 0 16px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                <span style={{ fontWeight: '500' }}>
                  {selectedDancers.length} danseur{selectedDancers.length > 1 ? 's' : ''}
                </span>
                {' • '}
                <span style={{ fontWeight: '500' }}>
                  {selectedStunts.length} stunt{selectedStunts.length > 1 ? 's' : ''}
                </span>
              </div>
              <button
                onClick={closeModal}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
              >
                ✓ Valider
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default StuntGroupList;
